import { useState, useEffect, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { useLanguage } from "@/contexts/LanguageContext";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Link from "@tiptap/extension-link";
import Underline from "@tiptap/extension-underline";
import Placeholder from "@tiptap/extension-placeholder";
import axios from "axios";
import {
  Sparkles, Copy, Save, Loader2, TrendingUp, ImageIcon,
  Tag, FileText, Globe, Search, Check, Bold, Italic, 
  Underline as UnderlineIcon, List, ListOrdered, Heading2, Heading3,
  Link2, Unlink, Undo, Redo, Eye, FileCode, X, Send,
  FolderOpen, Upload, User, Plus, ExternalLink, Lightbulb, PenTool, Info, Rss, Calendar, Clock, RefreshCw
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SafeHtml } from "@/components/SafeHtml";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Slider } from "@/components/ui/slider";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { Switch } from "@/components/ui/switch";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from "@/components/ui/command";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// MenuBar dla edytora WYSIWYG
const MenuBar = ({ editor, onInsertLink }) => {
  const [linkUrl, setLinkUrl] = useState("");
  const [linkText, setLinkText] = useState("");
  const [linkOpen, setLinkOpen] = useState(false);

  if (!editor) return null;

  const setLink = () => {
    if (linkUrl) {
      if (linkText && !editor.state.selection.empty === false) {
        editor.chain().focus().insertContent(`<a href="${linkUrl}">${linkText}</a>`).run();
      } else {
        editor.chain().focus().extendMarkRange("link").setLink({ href: linkUrl }).run();
      }
    }
    setLinkUrl("");
    setLinkText("");
    setLinkOpen(false);
  };

  const buttonClass = (active) =>
    `h-8 w-8 p-0 ${active ? "bg-blue-100 text-blue-700" : "hover:bg-slate-100"}`;

  return (
    <div className="flex flex-wrap items-center gap-1 p-2 border-b bg-slate-50 rounded-t-lg">
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBold().run()}
        className={buttonClass(editor.isActive("bold"))} title="Pogrubienie">
        <Bold className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleItalic().run()}
        className={buttonClass(editor.isActive("italic"))} title="Kursywa">
        <Italic className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleUnderline().run()}
        className={buttonClass(editor.isActive("underline"))} title="Podkreslenie">
        <UnderlineIcon className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 2 }).run()}
        className={buttonClass(editor.isActive("heading", { level: 2 }))} title="Naglowek H2">
        <Heading2 className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleHeading({ level: 3 }).run()}
        className={buttonClass(editor.isActive("heading", { level: 3 }))} title="Naglowek H3">
        <Heading3 className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={buttonClass(editor.isActive("bulletList"))} title="Lista punktowana">
        <List className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={buttonClass(editor.isActive("orderedList"))} title="Lista numerowana">
        <ListOrdered className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Popover open={linkOpen} onOpenChange={setLinkOpen}>
        <PopoverTrigger asChild>
          <Button variant="ghost" size="sm" className={buttonClass(editor.isActive("link"))} title="Dodaj link">
            <Link2 className="w-4 h-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-80 p-3">
          <div className="space-y-3">
            <p className="text-xs font-medium">Dodaj link</p>
            <div className="space-y-2">
              <Input placeholder="Tekst linku (opcjonalnie)" value={linkText}
                onChange={(e) => setLinkText(e.target.value)} className="h-8 text-xs" />
              <Input placeholder="https://example.com" value={linkUrl}
                onChange={(e) => setLinkUrl(e.target.value)} className="h-8 text-xs"
                onKeyDown={(e) => e.key === "Enter" && setLink()} />
            </div>
            <Button size="sm" className="w-full h-8 text-xs" onClick={setLink}>Wstaw link</Button>
          </div>
        </PopoverContent>
      </Popover>
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().unsetLink().run()}
        disabled={!editor.isActive("link")} className={buttonClass(false)} title="Usun link">
        <Unlink className="w-4 h-4" />
      </Button>
      
      <div className="w-px h-6 bg-slate-200 mx-1" />
      
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().undo().run()}
        disabled={!editor.can().undo()} className={buttonClass(false)} title="Cofnij">
        <Undo className="w-4 h-4" />
      </Button>
      <Button variant="ghost" size="sm" onClick={() => editor.chain().focus().redo().run()}
        disabled={!editor.can().redo()} className={buttonClass(false)} title="Ponow">
        <Redo className="w-4 h-4" />
      </Button>

      {onInsertLink && (
        <>
          <div className="w-px h-6 bg-slate-200 mx-1" />
          <Button variant="ghost" size="sm" onClick={onInsertLink}
            className="h-8 px-2 text-xs gap-1 text-emerald-700 hover:bg-emerald-50" title="Wstaw link reklamodawcy">
            <Link2 className="w-3.5 h-3.5" /> Link
          </Button>
        </>
      )}
    </div>
  );
};

export default function ContentGenerator() {
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  
  // Glowny tryb: "generate" | "publish"
  const [mainMode, setMainMode] = useState("generate");
  
  // Dane z trendu (przekazane przez URL)
  const [trendData, setTrendData] = useState(null);
  
  // Wspolne stany
  const [sites, setSites] = useState([]);
  const [generating, setGenerating] = useState(false);
  const [loadingTrends, setLoadingTrends] = useState(false);
  const [result, setResult] = useState(null);
  const [trendSuggestions, setTrendSuggestions] = useState(null);
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduling, setScheduling] = useState(false);
  const [previewMode, setPreviewMode] = useState("preview");
  
  // Formularz generowania
  const [form, setForm] = useState({
    topic: "", language: "pl", tone: "professional",
    word_count: 800, seo_keywords: "", site_id: "", use_trends: false, category: "Technologia",
    gambling_ad: false, gambling_url: "", gambling_brand: "", gambling_dofollow: false, gambling_extras: [],
  });

  // ========== STANY DLA PUBLIKACJI ==========
  const [pubTitle, setPubTitle] = useState("");
  const [pubContent, setPubContent] = useState("");
  const [pubHtmlSource, setPubHtmlSource] = useState("");
  const [pubMetaTitle, setPubMetaTitle] = useState("");
  const [pubMetaDescription, setPubMetaDescription] = useState("");
  const [pubTags, setPubTags] = useState("");
  const [pubFeaturedImage, setPubFeaturedImage] = useState("");
  const [pubImageAlt, setPubImageAlt] = useState("");
  const [pubKeywordPhrase, setPubKeywordPhrase] = useState("");
  const [pubKeywordUrl, setPubKeywordUrl] = useState("");
  const [pubGamblingAd, setPubGamblingAd] = useState(false);
  const [pubGamblingBrand, setPubGamblingBrand] = useState("");
  const [pubGamblingDofollow, setPubGamblingDofollow] = useState(false);
  const [pubGamblingExtras, setPubGamblingExtras] = useState([]); // [{anchor, url}]
  const [pubSelectedSiteId, setPubSelectedSiteId] = useState("");
  const [siteSearchOpen, setSiteSearchOpen] = useState(false);
  const [siteSearchQuery, setSiteSearchQuery] = useState("");
  const [pubSelectedCategoryId, setPubSelectedCategoryId] = useState("");
  const [pubAuthorName, setPubAuthorName] = useState("");
  const [pubSupportArticles, setPubSupportArticles] = useState([]);
  const [pubEditorTab, setPubEditorTab] = useState("editor");
  const [pubSaving, setPubSaving] = useState(false);
  const [pubPublishing, setPubPublishing] = useState(false);
  const [pubScheduleDate, setPubScheduleDate] = useState(""); // Data planowanej publikacji
  const [pubScheduling, setPubScheduling] = useState(false); // Stan ładowania planowania
  const [pubAiPrompt, setPubAiPrompt] = useState(""); // Prompt dla AI
  const [pubSourceLanguage, setPubSourceLanguage] = useState("pl"); // Język źródła (dla RSS)
  const [pendingPostId, setPendingPostId] = useState(null); // ID artykułu RSS do oznaczenia jako opublikowany
  const [articleType, setArticleType] = useState("news"); // Typ artykułu: news, review, opinion, tutorial, report
  const [sourceInfo, setSourceInfo] = useState(null); // Informacje o źródle RSS
  const [generatingSeoMeta, setGeneratingSeoMeta] = useState(false); // Stan ładowania generowania SEO
  
  // Sugestie linków wewnętrznych AI
  const [linkSuggestions, setLinkSuggestions] = useState([]);
  const [loadingLinkSuggestion, setLoadingLinkSuggestion] = useState(false);
  
  // Kategorie WP
  const [wpCategories, setWpCategories] = useState([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  
  // Media picker
  const [showImagePicker, setShowImagePicker] = useState(false);
  const [imagePickerTarget, setImagePickerTarget] = useState("pub"); // "pub" | "result"
  const [mediaImages, setMediaImages] = useState([]);
  const [mediaCategories, setMediaCategories] = useState([]);
  const [selectedMediaCategory, setSelectedMediaCategory] = useState("");
  const [loadingMedia, setLoadingMedia] = useState(false);
  const [uploadingImage, setUploadingImage] = useState(false);
  
  // Typy artykułów
  const articleTypes = [
    { value: "news", label: "Wiadomość / News" },
    { value: "report", label: "Raport / Relacja" },
    { value: "review", label: "Recenzja" },
    { value: "opinion", label: "Opinia / Komentarz" },
    { value: "tutorial", label: "Poradnik / How-to" },
    { value: "analysis", label: "Analiza" },
  ];
  
  // Post picker
  const [showPostPicker, setShowPostPicker] = useState(false);
  const [availablePosts, setAvailablePosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  
  // TipTap Editor dla publikacji
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ heading: { levels: [2, 3, 4] } }),
      Link.configure({
        openOnClick: false,
        autolink: true,
        HTMLAttributes: { 
          class: "text-blue-600 underline hover:text-blue-800",
          rel: "noopener noreferrer",
          target: "_blank",
        },
      }),
      Underline,
      Placeholder.configure({ placeholder: "Zacznij pisac artykul lub uzyj AI..." }),
    ],
    content: "",
    editorProps: {
      attributes: {
        class: "prose prose-sm sm:prose max-w-none focus:outline-none min-h-[350px] p-4",
      },
    },
    onUpdate: ({ editor }) => {
      setPubHtmlSource(editor.getHTML());
      setPubContent(editor.getHTML());
    },
  });

  const fetchSites = useCallback(async () => {
    try { setSites((await axios.get(`${API}/sites`)).data); } catch (err) { console.error(err); }
  }, []);

  useEffect(() => { fetchSites(); }, [fetchSites]);

  // Obsłuż dane z trendu przekazane przez URL lub sessionStorage (dla RSS)
  useEffect(() => {
    let trendDataParam = searchParams.get("trendData");
    
    // Jeśli brak w URL, sprawdź sessionStorage (używane przez RssFeeds)
    if (!trendDataParam && searchParams.get("fromRss")) {
      const sessionData = sessionStorage.getItem("pendingTrendData");
      if (sessionData) {
        trendDataParam = sessionData;
        // Wyczyść po odczytaniu
        sessionStorage.removeItem("pendingTrendData");
      }
    }
    
    if (trendDataParam) {
      try {
        const parsed = typeof trendDataParam === 'string' && trendDataParam.startsWith('{') 
          ? JSON.parse(trendDataParam)
          : JSON.parse(decodeURIComponent(trendDataParam));
        setTrendData(parsed);
        
        // Wypełnij formularz danymi z trendu
        setForm(prev => ({
          ...prev,
          topic: parsed.title || "",
          seo_keywords: parsed.keywords || "",
          category: parsed.category || prev.category,
          site_id: parsed.ai_suggested_site_id || "",
        }));
        
        // Ustaw język źródła jeśli przekazany (dla RSS w innym języku)
        if (parsed.source_language && parsed.source_language !== "pl") {
          setPubSourceLanguage(parsed.source_language);
        }
        
        // Ustaw prompt AI z opisem i źródłami
        let aiPrompt = "";
        if (parsed.description) {
          aiPrompt += `TEMAT: ${parsed.title}\n\n`;
          aiPrompt += `OPIS O CZYM NAPISAĆ:\n${parsed.description}\n\n`;
        }
        if (parsed.keywords) {
          aiPrompt += `SŁOWA KLUCZOWE: ${parsed.keywords}\n\n`;
        }
        if (parsed.enriched_sources?.length > 0) {
          aiPrompt += `ŹRÓDŁA (prawdziwe informacje do wykorzystania):\n`;
          parsed.enriched_sources.forEach((src, i) => {
            if (src.content) {
              aiPrompt += `\n--- Źródło ${i + 1}: ${src.url} ---\n`;
              aiPrompt += src.content.slice(0, 1500) + "\n";
            }
          });
          aiPrompt += `\nNAPISZ ARTYKUŁ BAZUJĄC NA POWYŻSZYCH ŹRÓDŁACH. NIE ZMYŚLAJ FAKTÓW.`;
        }
        if (parsed.source_urls?.length > 0 && !parsed.enriched_sources?.length) {
          aiPrompt += `LINKI DO PRZEANALIZOWANIA:\n${parsed.source_urls}\n`;
        }
        
        if (aiPrompt) {
          setPubAiPrompt(aiPrompt);
        }
        
        // Ustaw stronę docelową jeśli sugerowana
        if (parsed.ai_suggested_site_id) {
          setPubSelectedSiteId(parsed.ai_suggested_site_id);
        }
        
        // Zapisz ID artykułu RSS do oznaczenia jako opublikowany
        if (parsed.pending_post_id) {
          setPendingPostId(parsed.pending_post_id);
        }
        
        // Zapisz informacje o źródle RSS
        if (parsed.source_feed_name || parsed.source_urls) {
          setSourceInfo({
            feedName: parsed.source_feed_name,
            sourceUrl: parsed.source_urls,
            originalContent: parsed.original_content,
            imageUrl: parsed.image_url
          });
          
          // Ustaw zdjęcie z RSS jeśli dostępne
          if (parsed.image_url) {
            setPubFeaturedImage(parsed.image_url);
          }
        }
        
        // Ustaw tytuł z propozycji
        if (parsed.title) {
          setPubTitle(parsed.title);
        }
        
        toast.success("Załadowano dane z RSS - gotowe do generowania!");
      } catch (err) {
        console.error("Error parsing trend data:", err);
      }
    }
  }, [searchParams]);

  // Pobierz kategorie WP po wybraniu strony
  useEffect(() => {
    if (!pubSelectedSiteId) {
      setWpCategories([]);
      setPubSelectedCategoryId("");
      return;
    }
    
    const fetchCategories = async () => {
      setLoadingCategories(true);
      try {
        const res = await axios.get(`${API}/sites/wp/${pubSelectedSiteId}/wp-categories`);
        setWpCategories(res.data || []);
      } catch (err) {
        console.error("Error fetching WP categories:", err);
        setWpCategories([]);
      } finally {
        setLoadingCategories(false);
      }
    };
    fetchCategories();
  }, [pubSelectedSiteId]);

  // Smart generate z SEO + zdjecie
  const handleSmartGenerate = async () => {
    if (!form.topic.trim() && !form.use_trends) {
      toast.error("Podaj temat artykulu lub wlacz trendy");
      return;
    }
    setGenerating(true);
    setResult(null);
    try {
      // Przygotuj źródła z trendu
      let sourceContent = "";
      let sourceUrls = [];
      let trendDescription = "";
      
      if (trendData) {
        // Połącz treści ze źródeł
        if (trendData.enriched_sources?.length > 0) {
          sourceContent = trendData.enriched_sources
            .filter(s => s.content)
            .map(s => `[Źródło: ${s.url}]\n${s.content}`)
            .join("\n\n---\n\n");
        }
        if (trendData.source_urls) {
          sourceUrls = Array.isArray(trendData.source_urls) 
            ? trendData.source_urls 
            : trendData.source_urls.split("\n").filter(u => u.trim());
        }
        trendDescription = trendData.description || "";
      }
      
      const res = await axios.post(`${API}/generate/smart`, {
        ...form,
        seo_keywords: form.seo_keywords ? form.seo_keywords.split(",").map(k => k.trim()).filter(Boolean) : [],
        site_id: form.site_id || null,
        source_content: sourceContent,
        source_urls: sourceUrls,
        trend_description: trendDescription,
        gambling_dofollow: !!form.gambling_dofollow,
        extra_links: (form.gambling_extras || []).filter(x => x.anchor?.trim() && x.url?.trim()).map(x => ({anchor: x.anchor, url: x.url, dofollow: !!x.dofollow})),
      }, { timeout: 90000 });
      setResult(res.data);
      if (res.data.trend_suggestions) {
        setTrendSuggestions(res.data.trend_suggestions);
      }
      toast.success(t("common.success"));
    } catch (err) {
      toast.error(err.response?.data?.detail || t("common.error"));
    } finally { setGenerating(false); }
  };

  // Pobierz sugestie trendow
  const handleGetTrends = async () => {
    if (!form.site_id) { toast.error("Wybierz strone docelowa"); return; }
    setLoadingTrends(true);
    try {
      const res = await axios.post(`${API}/generate/smart`, {
        topic: "", site_id: form.site_id, use_trends: true,
        language: form.language, tone: form.tone, word_count: 100,
      }, { timeout: 60000 });
      if (res.data.trend_suggestions?.topics) {
        setTrendSuggestions(res.data.trend_suggestions);
        toast.success(`${res.data.trend_suggestions.topics.length} tematow zaproponowanych`);
      }
    } catch (err) { toast.error(t("common.error")); }
    finally { setLoadingTrends(false); }
  };

  const handleCopy = async () => {
    if (!result) return;
    try {
      await navigator.clipboard.writeText(`${result.title}\n\n${result.content}`);
      toast.success(t("generator.copyContent"));
    } catch {
      const ta = document.createElement("textarea");
      ta.value = `${result.title}\n\n${result.content}`;
      document.body.appendChild(ta); ta.select(); document.execCommand("copy"); document.body.removeChild(ta);
      toast.success(t("generator.copyContent"));
    }
  };

  // ========== FUNKCJE PUBLIKACJI ==========
  
  // Wstaw link reklamodawcy do tresci - zamienia pierwszą znalezioną frazę na link
  const insertKeywordLink = useCallback(() => {
    if (!editor || !pubKeywordPhrase || !pubKeywordUrl) {
      toast.error("Uzupelnij fraze i URL linku");
      return;
    }
    
    // Pobierz aktualną treść HTML
    const currentHtml = editor.getHTML();
    
    // Sprawdź czy fraza istnieje w treści (case-insensitive)
    const phraseRegex = new RegExp(`(${pubKeywordPhrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'i');
    
    if (!phraseRegex.test(currentHtml)) {
      // Fraza nie istnieje - wstaw za pierwszym nagłówkiem h2/h3
      const h2Match = currentHtml.match(/<\/h[23]>/i);
      if (h2Match) {
        const insertPos = h2Match.index + h2Match[0].length;
        const newHtml = currentHtml.slice(0, insertPos) + 
          `<p>Więcej informacji znajdziesz na stronie <a href="${pubKeywordUrl}">${pubKeywordPhrase}</a>.</p>` + 
          currentHtml.slice(insertPos);
        editor.commands.setContent(newHtml);
        toast.success("Link wstawiony za pierwszym nagłówkiem");
      } else {
        // Brak nagłówka - wstaw na początku pierwszego paragrafu
        const pMatch = currentHtml.match(/<p>/i);
        if (pMatch) {
          const insertPos = pMatch.index + pMatch[0].length;
          const newHtml = currentHtml.slice(0, insertPos) + 
            `Sprawdź także <a href="${pubKeywordUrl}">${pubKeywordPhrase}</a>. ` + 
            currentHtml.slice(insertPos);
          editor.commands.setContent(newHtml);
          toast.success("Link wstawiony na początku treści");
        } else {
          // Fallback - dodaj na końcu
          editor.chain().focus().insertContent(`<p><a href="${pubKeywordUrl}">${pubKeywordPhrase}</a></p>`).run();
          toast.info("Link dodany na końcu (brak miejsca w treści)");
        }
      }
      return;
    }
    
    // Fraza istnieje - zamień PIERWSZĄ na link (tylko pierwszą, żeby nie zaśmiecać)
    const newHtml = currentHtml.replace(phraseRegex, `<a href="${pubKeywordUrl}">$1</a>`);
    editor.commands.setContent(newHtml);
    toast.success(`Fraza "${pubKeywordPhrase}" zamieniona na link`);
  }, [editor, pubKeywordPhrase, pubKeywordUrl]);

  // Pobierz sugestię linku wewnętrznego od AI
  const fetchLinkSuggestion = async () => {
    if (!pubTitle.trim() && !pubAiPrompt.trim()) {
      toast.error("Wpisz tytuł lub temat artykułu");
      return;
    }
    
    setLoadingLinkSuggestion(true);
    setLinkSuggestions([]);
    
    try {
      const res = await axios.post(`${API}/generate/suggest-internal-link`, {
        title: pubTitle,
        topic: pubAiPrompt,
        content: pubContent?.substring(0, 500) || "",
        site_id: pubSelectedSiteId || null
      });
      
      // Sprawdź czy cache jest pusty
      if (res.data.cache_empty) {
        toast.warning("Baza linków pusta. Odświeżam...");
        await refreshLinkCache();
        return;
      }
      
      if (res.data.suggestions && res.data.suggestions.length > 0) {
        setLinkSuggestions(res.data.suggestions);
        toast.success(`AI zaproponowało ${res.data.suggestions.length} link${res.data.suggestions.length > 1 ? 'i' : ''}`);
      } else {
        toast.info(res.data.message || "Brak pasujących linków");
      }
    } catch (err) {
      toast.error("Błąd pobierania sugestii: " + (err.response?.data?.detail || err.message));
    } finally {
      setLoadingLinkSuggestion(false);
    }
  };

  // Odśwież cache linków (pobiera z RSS wszystkich stron)
  const [refreshingCache, setRefreshingCache] = useState(false);
  const refreshLinkCache = async () => {
    setRefreshingCache(true);
    try {
      const res = await axios.post(`${API}/generate/refresh-link-cache`);
      toast.success(res.data.message || `Pobrano ${res.data.total_links} linków`);
      // Automatycznie ponów sugestię
      if (pubTitle.trim() || pubAiPrompt.trim()) {
        setTimeout(() => fetchLinkSuggestion(), 500);
      }
    } catch (err) {
      toast.error("Błąd odświeżania: " + (err.response?.data?.detail || err.message));
    } finally {
      setRefreshingCache(false);
    }
  };

  // Zastosuj sugerowany link
  const applySuggestedLink = (suggestion) => {
    setPubKeywordPhrase(suggestion.anchor_text);
    setPubKeywordUrl(suggestion.url);
    toast.success(`Ustawiono: "${suggestion.anchor_text}" → ${suggestion.target_name || suggestion.url}`);
  };

  // Zastosuj zmiany HTML
  const applyHtmlChanges = () => {
    if (editor) editor.commands.setContent(pubHtmlSource);
    setPubContent(pubHtmlSource);
  };

  // Pobierz media
  const fetchMediaImages = async (category = "") => {
    setLoadingMedia(true);
    try {
      const url = category ? `${API}/media?category=${encodeURIComponent(category)}` : `${API}/media`;
      const res = await axios.get(url);
      setMediaImages(res.data || []);
    } catch (err) {
      console.error(err);
      setMediaImages([]);
    } finally {
      setLoadingMedia(false);
    }
  };

  const fetchMediaCategories = async () => {
    try {
      const res = await axios.get(`${API}/media/categories`);
      setMediaCategories(res.data.categories || []);
    } catch (err) {
      console.error(err);
    }
  };

  const openImagePicker = (target = "pub") => {
    setImagePickerTarget(target);
    setShowImagePicker(true);
    fetchMediaCategories();
    fetchMediaImages(selectedMediaCategory);
  };

  const selectMediaImage = (img) => {
    const imgUrl = img.url?.startsWith("http") ? img.url : `https://epidemos.pl${img.url}`;
    
    if (imagePickerTarget === "result") {
      // Dla wygenerowanego artykułu
      setResult(prev => ({
        ...prev,
        featured_image: {
          url: imgUrl,
          filename: img.filename || img.name || "selected_image.jpg",
          alt: img.alt || prev?.title || "",
          tags: img.tags || []
        }
      }));
      toast.success("Zdjęcie główne zmienione");
    } else {
      // Dla ręcznej publikacji
      setPubFeaturedImage(imgUrl);
      toast.success("Obrazek wybrany");
    }
    setShowImagePicker(false);
  };

  // Wybierz zdjęcie dla wygenerowanego artykułu
  const selectMediaImageForResult = (img) => {
    const imgUrl = img.url?.startsWith("http") ? img.url : `https://epidemos.pl${img.url}`;
    setResult(prev => ({
      ...prev,
      featured_image: {
        url: imgUrl,
        filename: img.filename || img.name || "selected_image.jpg",
        alt: img.alt || prev?.title || "",
        tags: img.tags || []
      }
    }));
    setShowImagePicker(false);
    toast.success("Zdjęcie główne zmienione");
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (!file.type.startsWith("image/")) {
      toast.error("Wybierz plik obrazka");
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toast.error("Maksymalny rozmiar pliku to 10MB");
      return;
    }
    
    setUploadingImage(true);
    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        const dataUrl = event.target.result;
        
        const res = await axios.post(`${API}/media/bulk-upload`, {
          files: [{ filename: file.name, data_url: dataUrl }],
          category: selectedMediaCategory || "Artykuly"
        });
        
        if (res.data.uploaded > 0 && res.data.items?.length > 0) {
          const uploaded = res.data.items[0];
          const imgUrl = uploaded.url?.startsWith("http") ? uploaded.url : `https://epidemos.pl${uploaded.url}`;
          
          if (imagePickerTarget === "result") {
            // Dla wygenerowanego artykułu
            setResult(prev => ({
              ...prev,
              featured_image: {
                url: imgUrl,
                filename: uploaded.filename || file.name,
                alt: prev?.title || "",
                tags: []
              }
            }));
            toast.success("Zdjęcie wgrane i ustawione jako główne");
          } else {
            // Dla ręcznej publikacji
            setPubFeaturedImage(imgUrl);
            toast.success("Obrazek wgrany i wybrany");
          }
          setShowImagePicker(false);
        } else {
          toast.error("Blad uploadu");
        }
        setUploadingImage(false);
      };
      reader.readAsDataURL(file);
    } catch (err) {
      toast.error("Blad uploadu: " + (err.response?.data?.detail || err.message));
      setUploadingImage(false);
    }
  };

  // Pobierz posty do linkowania
  const fetchAvailablePosts = async () => {
    setLoadingPosts(true);
    try {
      const res = await axios.get(`${API}/posts?status=published&limit=50`);
      setAvailablePosts(res.data || []);
    } catch (err) {
      console.error(err);
      setAvailablePosts([]);
    } finally {
      setLoadingPosts(false);
    }
  };

  const addSupportArticle = (post) => {
    if (!pubSupportArticles.find(a => a.id === post.id)) {
      setPubSupportArticles([...pubSupportArticles, post]);
    }
    setShowPostPicker(false);
  };

  const removeSupportArticle = (postId) => {
    setPubSupportArticles(pubSupportArticles.filter(a => a.id !== postId));
  };

  // Generuj metadane SEO z AI
  const generateSeoMetaWithAI = async () => {
    if (!pubTitle.trim() && !pubContent.trim()) {
      toast.error("Najpierw wpisz tytuł lub treść artykułu");
      return;
    }
    
    setGeneratingSeoMeta(true);
    try {
      const res = await axios.post(`${API}/generate/seo-meta`, {
        title: pubTitle,
        content: pubContent || pubHtmlSource,
        language: pubSourceLanguage || "pl",
        ai_model: "grok"
      });
      
      if (res.data?.success) {
        setPubMetaTitle(res.data.meta_title || "");
        setPubMetaDescription(res.data.meta_description || "");
        setPubTags(res.data.tags || "");
        setPubImageAlt(res.data.image_alt || "");
        toast.success("Metadane SEO wygenerowane!");
      } else {
        toast.error("Nie udało się wygenerować metadanych");
      }
    } catch (err) {
      console.error(err);
      toast.error("Błąd generowania: " + (err.response?.data?.detail || err.message));
    } finally {
      setGeneratingSeoMeta(false);
    }
  };

  // Zapisz szkic
  const handleSaveDraft = async () => {
    if (!pubTitle.trim()) {
      toast.error("Tytul jest wymagany");
      return;
    }
    
    setPubSaving(true);
    try {
      const payload = {
        title: pubTitle,
        content: pubContent || pubHtmlSource,
        meta_title: pubMetaTitle || pubTitle.substring(0, 60),
        meta_description: pubMetaDescription,
        tags: pubTags,
        featured_image: pubFeaturedImage,
        image_alt: pubImageAlt,
        site_id: pubSelectedSiteId,
        wp_category_ids: pubSelectedCategoryId ? [pubSelectedCategoryId] : [],
        author_name: pubAuthorName,
        keyword_phrase: pubKeywordPhrase,
        keyword_url: pubKeywordUrl,
        support_article_ids: pubSupportArticles.map(a => a.id),
        status: "draft"
      };
      
      await axios.post(`${API}/admin/articles`, payload);
      toast.success("Artykul zapisany jako szkic");
    } catch (err) {
      toast.error("Blad zapisu: " + (err.response?.data?.detail || err.message));
    } finally {
      setPubSaving(false);
    }
  };

  // Publikuj
  const handlePublish = async () => {
    if (!pubTitle.trim()) {
      toast.error("Tytul jest wymagany");
      return;
    }
    if (!pubSelectedSiteId) {
      toast.error("Wybierz strone do publikacji");
      return;
    }
    if (!pubContent && !pubHtmlSource) {
      toast.error("Tresc artykulu jest wymagana");
      return;
    }
    
    setPubPublishing(true);
    try {
      const payload = {
        title: pubTitle,
        content: pubContent || pubHtmlSource,
        meta_title: pubMetaTitle || pubTitle.substring(0, 60),
        meta_description: pubMetaDescription,
        tags: pubTags,
        featured_image: pubFeaturedImage,
        image_alt: pubImageAlt,
        site_id: pubSelectedSiteId,
        wp_category_ids: pubSelectedCategoryId ? [pubSelectedCategoryId] : [],
        author_name: pubAuthorName,
        keyword_phrase: pubKeywordPhrase,
        keyword_url: pubKeywordUrl,
        support_article_ids: pubSupportArticles.map(a => a.id),
        status: "published"
      };
      
      const res = await axios.post(`${API}/admin/articles/publish`, payload);
      
      if (res.data.published_url) {
        toast.success(
          <div>
            <p>Artykul opublikowany!</p>
            <a href={res.data.published_url} target="_blank" rel="noopener noreferrer" 
               className="text-blue-600 underline text-sm">
              {res.data.published_url}
            </a>
          </div>
        );
      } else {
        toast.success("Artykul opublikowany");
      }
      
      // Oznacz artykuł RSS jako opublikowany
      if (pendingPostId) {
        try {
          await axios.patch(`${API}/rss-feeds/pending/${pendingPostId}`, { status: "published" });
        } catch (e) {
          console.error("Could not mark pending post as published:", e);
        }
      }
      
      // Reset formularza
      setPubTitle("");
      setPubContent("");
      setPubHtmlSource("");
      editor?.commands.setContent("");
      setPubMetaTitle("");
      setPubMetaDescription("");
      setPubTags("");
      setPubFeaturedImage("");
      setPubImageAlt("");
      setPubKeywordPhrase("");
      setPubKeywordUrl("");
      setPubSelectedCategoryId("");
      setPubAuthorName("");
      setPubSupportArticles([]);
      setPendingPostId(null);
      setSourceInfo(null);  // Wyczyść info o źródle
      setArticleType("news");
    } catch (err) {
      toast.error("Blad publikacji: " + (err.response?.data?.detail || err.message));
    } finally {
      setPubPublishing(false);
    }
  };

  // Zaplanuj publikacje na pozniej
  const handleSchedulePost = async () => {
    if (!pubTitle.trim()) {
      toast.error("Tytul jest wymagany");
      return;
    }
    if (!pubSelectedSiteId) {
      toast.error("Wybierz strone do publikacji");
      return;
    }
    if (!pubContent && !pubHtmlSource) {
      toast.error("Tresc artykulu jest wymagana");
      return;
    }
    if (!pubScheduleDate) {
      toast.error("Wybierz date i godzine publikacji");
      return;
    }
    
    // Sprawdz czy data jest w przyszlosci
    const scheduledTime = new Date(pubScheduleDate);
    if (scheduledTime <= new Date()) {
      toast.error("Data publikacji musi byc w przyszlosci");
      return;
    }
    
    setPubScheduling(true);
    try {
      const payload = {
        title: pubTitle,
        content: pubContent || pubHtmlSource,
        meta_title: pubMetaTitle || pubTitle.substring(0, 60),
        meta_description: pubMetaDescription,
        tags: pubTags,
        featured_image: pubFeaturedImage,
        image_alt: pubImageAlt,
        site_id: pubSelectedSiteId,
        wp_category_ids: pubSelectedCategoryId ? [pubSelectedCategoryId] : [],
        author_name: pubAuthorName,
        keyword_phrase: pubKeywordPhrase,
        keyword_url: pubKeywordUrl,
        support_article_ids: pubSupportArticles.map(a => a.id),
        status: "published",
        publication_date: pubScheduleDate  // Data planowanej publikacji
      };
      
      const res = await axios.post(`${API}/admin/articles/publish`, payload);
      
      if (res.data.scheduled) {
        const scheduledDate = new Date(res.data.scheduled_at).toLocaleString("pl-PL", {
          day: "2-digit",
          month: "2-digit",
          year: "numeric",
          hour: "2-digit",
          minute: "2-digit"
        });
        toast.success(
          <div>
            <p className="font-medium">Artykul zaplanowany!</p>
            <p className="text-sm text-muted-foreground">Publikacja: {scheduledDate}</p>
          </div>
        );
      } else {
        toast.success("Artykul dodany do kolejki");
      }
      
      // Oznacz artykuł RSS jako opublikowany
      if (pendingPostId) {
        try {
          await axios.patch(`${API}/rss-feeds/pending/${pendingPostId}`, { status: "published" });
        } catch (e) {
          console.error("Could not mark pending post as published:", e);
        }
      }
      
      // Reset formularza
      setPubTitle("");
      setPubContent("");
      setPubHtmlSource("");
      editor?.commands.setContent("");
      setPubMetaTitle("");
      setPubMetaDescription("");
      setPubTags("");
      setPubFeaturedImage("");
      setPubImageAlt("");
      setPubKeywordPhrase("");
      setPubKeywordUrl("");
      setPubSelectedCategoryId("");
      setPubAuthorName("");
      setPubSupportArticles([]);
      setPendingPostId(null);
      setSourceInfo(null);
      setArticleType("news");
      setPubScheduleDate("");
    } catch (err) {
      toast.error("Blad planowania: " + (err.response?.data?.detail || err.message));
    } finally {
      setPubScheduling(false);
    }
  };

  // AI - Wygeneruj artykul dla publikacji
  const handleGenerateForPublish = async () => {
    if (!pubTitle.trim() && !pubAiPrompt.trim()) {
      toast.error("Podaj tytul lub instrukcje dla AI");
      return;
    }
    setGenerating(true);
    try {
      // Przygotuj rozszerzony prompt dla AI
      let fullPrompt = pubAiPrompt.trim();
      
      // Dodaj typ artykułu do promptu
      const typeLabel = articleTypes.find(t => t.value === articleType)?.label || "Wiadomość";
      fullPrompt = `TYP ARTYKUŁU: ${typeLabel}\n\n` + fullPrompt;
      
      // Jeśli to artykuł z RSS - dodaj ważne instrukcje
      if (sourceInfo) {
        fullPrompt += `\n\nWAŻNE INSTRUKCJE:
- Pisz TYLKO na podstawie podanych informacji źródłowych. NIE ZMYŚLAJ faktów!
- Zachowaj zgodność z faktami z oryginalnego źródła.
- Na końcu artykułu dodaj źródło w formacie: <p class="source-info"><em>Źródło: ${sourceInfo.feedName || 'RSS'}</em></p>
- Artykuł ma być napisany w stylu: ${typeLabel}.`;
      }
      
      // Jeśli są fraza kluczowa i link, dodaj je do promptu
      if (pubKeywordPhrase && pubKeywordUrl) {
        fullPrompt += fullPrompt ? "\n\n" : "";
        fullPrompt += `WAŻNE: W artykule umiesc naturalnie link do strony ${pubKeywordUrl} z anchor textem "${pubKeywordPhrase}". Link powinien byc wpleciony w tresc w sposob naturalny.`;
      }
      
      const res = await axios.post(`${API}/admin/blog/generate`, { 
        topic: pubTitle || "Artykul na podstawie instrukcji", 
        ai_model: "gemini",
        custom_prompt: fullPrompt || null,
        keyword_phrase: pubKeywordPhrase || null,
        keyword_url: pubKeywordUrl || null,
        source_language: pubSourceLanguage !== "pl" ? pubSourceLanguage : null,
        target_language: "pl",
        article_type: articleType,
        source_feed_name: sourceInfo?.feedName || null,
        gambling_ad: pubGamblingAd,
        gambling_url: pubKeywordUrl || "",
        gambling_brand: pubGamblingBrand || "",
        gambling_dofollow: pubGamblingDofollow,
        extra_links: pubGamblingExtras.filter(x => x.anchor.trim() && x.url.trim()).map(x => ({anchor: x.anchor, url: x.url, dofollow: !!x.dofollow})),
      });
      const data = res.data;
      
      setPubTitle(data.title || pubTitle);
      setPubMetaTitle(data.meta_title || data.title?.substring(0, 60) || "");
      setPubMetaDescription(data.meta_description || "");
      setPubTags(data.tags || "");
      setPubImageAlt(data.image_alt || data.title || ""); // Auto-fill image alt
      
      if (editor && data.content) {
        editor.commands.setContent(data.content);
        setPubHtmlSource(data.content);
        setPubContent(data.content);
      }
      
      toast.success(`Wygenerowano artykul (${data.word_count || 0} slow)`);
    } catch (err) {
      toast.error("Blad generowania: " + (err.response?.data?.detail || err.message));
    } finally {
      setGenerating(false);
    }
  };

  // Statystyki tresci
  const textContent = pubHtmlSource.replace(/<[^>]+>/g, "");
  const charCount = textContent.length;
  const wordCount = textContent.split(/\s+/).filter(w => w).length;

  // Filtruj strony z WP API dla publikacji
  const sitesWithWpApi = sites.filter(s => s.wp_api_user || s.site_type === "emergent" || s.emergent_api_url);

  return (
    <div data-testid="generator-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-4xl font-bold tracking-tight text-foreground">
            {t("generator.title")}
          </h1>
          <p className="text-sm text-muted-foreground mt-1 font-body">{t("generator.subtitle")}</p>
        </div>
      </div>

      {/* Baner z danymi z trendu */}
      {trendData && (
        <Card className="rounded-xl border-2 border-primary/30 bg-primary/5">
          <CardContent className="py-4">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-primary/10">
                <TrendingUp className="w-5 h-5 text-primary" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <h3 className="font-semibold text-foreground">Temat z Hitowych Tematów</h3>
                  <Badge variant="secondary">{trendData.category}</Badge>
                </div>
                <p className="text-sm font-medium text-foreground">{trendData.title}</p>
                {trendData.description && (
                  <p className="text-xs text-muted-foreground mt-1 line-clamp-2">{trendData.description}</p>
                )}
                {trendData.enriched_sources?.length > 0 && (
                  <div className="flex items-center gap-1 mt-2 text-xs text-emerald-600">
                    <Check className="w-3 h-3" />
                    <span>{trendData.enriched_sources.length} źródeł załadowanych do AI</span>
                  </div>
                )}
                {trendData.ai_suggested_site_id && (
                  <p className="text-xs text-muted-foreground mt-1">
                    <Globe className="w-3 h-3 inline mr-1" />
                    Sugerowana strona: {sites.find(s => s.id === trendData.ai_suggested_site_id)?.name || trendData.ai_suggested_site_id}
                  </p>
                )}
              </div>
              <Button variant="ghost" size="icon" className="h-7 w-7"
                onClick={() => { setTrendData(null); setForm(prev => ({ ...prev, topic: "", seo_keywords: "" })); }}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Glowne zakladki: Generuj AI / Recznie publikuj */}
      <Tabs value={mainMode} onValueChange={setMainMode} className="w-full">
        <TabsList className="w-full justify-start h-auto p-1 bg-slate-100 dark:bg-slate-800 rounded-lg">
          <TabsTrigger value="generate" className="gap-2 px-4 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900">
            <Sparkles className="w-4 h-4" /> Generuj z AI
          </TabsTrigger>
          <TabsTrigger value="publish" className="gap-2 px-4 py-2 data-[state=active]:bg-white dark:data-[state=active]:bg-slate-900">
            <PenTool className="w-4 h-4" /> Recznie publikuj
          </TabsTrigger>
        </TabsList>

        {/* ========== ZAKLADKA: GENERUJ Z AI ========== */}
        <TabsContent value="generate" className="mt-4">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            {/* Panel kontrolny */}
            <Card className="lg:col-span-2 rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-3">
                <CardTitle className="text-base font-heading flex items-center gap-2">
                  <Sparkles className="w-4 h-4 text-primary" strokeWidth={1.5} />
                  Konfiguracja
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4 pt-0">
                {/* Strona docelowa */}
                {sites.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t("generator.targetSite")}</Label>
                    <Select value={form.site_id || "none"} onValueChange={(v) => {
                      const newSiteId = v === "none" ? "" : v;
                      setForm({ ...form, site_id: newSiteId });
                      if (newSiteId) {
                        const site = sites.find(s => s.id === newSiteId);
                        if (site?.category) setForm(f => ({ ...f, site_id: newSiteId, category: site.category }));
                      }
                    }}>
                      <SelectTrigger data-testid="site-select"><SelectValue placeholder={t("generator.selectSite")} /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">-- {t("generator.selectSite")} --</SelectItem>
                        {sites.map(s => <SelectItem key={s.id} value={s.id}>{s.name} {s.category ? `(${s.category})` : ""}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Kategoria tematyczna */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">Kategoria tematyczna</Label>
                  <Select value={form.category || "Technologia"} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {["Technologia", "Biznes", "Finanse", "Bankowosc", "Zdrowie", "Medycyna",
                        "Moda", "Uroda", "Kosmetyki", "Podroze", "Turystyka", "Motoryzacja",
                        "Sport", "Fitness", "Edukacja", "Marketing", "SEO", "E-commerce",
                        "Nieruchomosci", "Prawo", "Ekologia", "Jedzenie", "Kultura", "Rozrywka",
                        "Gaming", "AI/ML", "Lifestyle", "Dom i Ogrod", "Parenting", "Zwierzeta"
                      ].map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>

                {/* Zaproponuj tematy z trendami */}
                {form.site_id && (
                  <Button variant="outline" className="w-full gap-2 text-xs" onClick={handleGetTrends}
                    disabled={loadingTrends} data-testid="trends-suggest-btn">
                    {loadingTrends ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <TrendingUp className="w-3.5 h-3.5" />}
                    Zaproponuj tematy z trendami dla tej domeny
                  </Button>
                )}

                {/* Sugestie trendow */}
                {trendSuggestions?.topics && (
                  <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 space-y-1.5">
                    <p className="text-[10px] font-semibold text-amber-700 dark:text-amber-400 uppercase tracking-wider">Sugerowane tematy</p>
                    {trendSuggestions.topics.map((tr, i) => (
                      <button key={i} onClick={() => {
                        setForm({ ...form, topic: tr.title, tone: tr.tone || form.tone });
                        setSelectedTrend(i);
                      }}
                        className={`w-full text-left p-2 rounded-md transition-colors ${
                          selectedTrend === i ? "bg-primary/10 border border-primary/30 ring-1 ring-primary/20" : "hover:bg-amber-100 dark:hover:bg-amber-900/20"
                        }`}>
                        <div className="flex items-center gap-2">
                          {selectedTrend === i && <Check className="w-3.5 h-3.5 text-primary shrink-0" strokeWidth={2} />}
                          <div>
                            <p className={`text-xs font-medium ${selectedTrend === i ? "text-primary" : "text-foreground"}`}>{tr.title}</p>
                            <p className="text-[9px] text-muted-foreground">{tr.reason} | Ton: {tr.tone}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                )}

                {/* Temat */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("generator.topic")}</Label>
                  <Textarea data-testid="topic-input" value={form.topic}
                    onChange={(e) => setForm({ ...form, topic: e.target.value })}
                    placeholder={t("generator.topicPlaceholder")} rows={3} className="resize-none" />
                </div>

                {/* Jezyk / Ton */}
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t("generator.language")}</Label>
                    <Select value={form.language} onValueChange={(v) => setForm({ ...form, language: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="pl">Polski</SelectItem>
                        <SelectItem value="en">English</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-sm font-medium">{t("generator.tone")}</Label>
                    <Select value={form.tone} onValueChange={(v) => setForm({ ...form, tone: v })}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="professional">{t("generator.toneOptions.professional")}</SelectItem>
                        <SelectItem value="casual">{t("generator.toneOptions.casual")}</SelectItem>
                        <SelectItem value="seo">{t("generator.toneOptions.seo")}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                {/* Liczba slow */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">
                    {t("generator.wordCount")}: <span className="text-primary font-bold">{form.word_count}</span>
                  </Label>
                  <Slider value={[form.word_count]} onValueChange={([v]) => setForm({ ...form, word_count: v })} min={200} max={3000} step={100} />
                </div>

                {/* Keywords */}
                <div className="space-y-2">
                  <Label className="text-sm font-medium">{t("generator.keywords")}</Label>
                  <Input data-testid="keywords-input" value={form.seo_keywords}
                    onChange={(e) => setForm({ ...form, seo_keywords: e.target.value })}
                    placeholder={t("generator.keywordsPlaceholder")} />
                </div>

                <Separator />

                {/* Sprint 8.L: Gambling mode */}
                <div className="rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
                  <label className="flex items-start gap-2 cursor-pointer">
                    <input type="checkbox" className="mt-0.5"
                      data-testid="gambling-checkbox"
                      checked={form.gambling_ad}
                      onChange={(e) => setForm({ ...form, gambling_ad: e.target.checked })} />
                    <div className="flex-1">
                      <div className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                        &#127920; Reklama strony hazardowej
                      </div>
                      <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5 leading-snug">
                        Wymusza tryb edukacyjny (historia, matematyka, jurysdykcje) z linkiem inline,
                        oznaczenie sponsored + disclaimer 18+ + info o odpowiedzialnej grze.
                        <strong> Uwaga: publikacja z linkiem do operatora bez zezwolenia MF PL wiaze sie z ryzykiem prawnym (art. 29 ustawy o grach hazardowych).</strong>
                      </p>
                    </div>
                  </label>
                  {form.gambling_ad && (
                    <div className="space-y-2 pt-2 border-t border-amber-200">
                      <div>
                        <Label className="text-xs font-medium">URL operatora (link afiliacyjny)</Label>
                        <Input value={form.gambling_url}
                          onChange={(e) => setForm({ ...form, gambling_url: e.target.value })}
                          placeholder="https://track.blackflagaffiliates.com/visit/?bta=..." />
                      </div>
                      <div>
                        <Label className="text-xs font-medium">Nazwa marki / operatora</Label>
                        <Input value={form.gambling_brand}
                          onChange={(e) => setForm({ ...form, gambling_brand: e.target.value })}
                          placeholder="np. WinAir" />
                      </div>
                      <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
                        <input type="checkbox" checked={!!form.gambling_dofollow}
                          onChange={(e) => setForm({ ...form, gambling_dofollow: e.target.checked })} />
                        <span><strong>Dofollow</strong> (indeksowany link — bez atrybutu rel)</span>
                      </label>
                      <p className="text-[10px] text-amber-600 leading-tight">
                        Link zostanie wstawiony jako <code>rel="sponsored nofollow noopener"</code>
                        w sekcji &quot;{'{'}Brand{'}'} w kontekscie rynku&quot;.
                      </p>

                      <div className="pt-2 border-t border-amber-200 space-y-2">
                        <div className="flex items-center justify-between">
                          <Label className="text-xs font-medium">Dodatkowe linki afiliacyjne (opcjonalne)</Label>
                          <Button size="sm" variant="outline"
                            onClick={() => setForm({...form, gambling_extras: [...(form.gambling_extras||[]), {anchor: "", url: ""}]})}
                            className="h-7 text-[11px] gap-1">
                            <Plus className="w-3 h-3" /> Dodaj link
                          </Button>
                        </div>
                        {(form.gambling_extras || []).length === 0 && (
                          <p className="text-[10px] text-amber-600">Brak dodatkowych. Kliknij &quot;Dodaj link&quot; aby dodac.</p>
                        )}
                        {(form.gambling_extras || []).map((it, idx) => (
                          <div key={idx} className="flex gap-1.5 items-start bg-white/50 rounded p-2">
                            <div className="flex-1 space-y-1">
                              <Input value={it.anchor}
                                onChange={(e) => {
                                  const next = [...(form.gambling_extras||[])];
                                  next[idx] = {...next[idx], anchor: e.target.value};
                                  setForm({...form, gambling_extras: next});
                                }}
                                placeholder="Anchor text"
                                className="text-xs h-8" />
                              <Input value={it.url}
                                onChange={(e) => {
                                  const next = [...(form.gambling_extras||[])];
                                  next[idx] = {...next[idx], url: e.target.value};
                                  setForm({...form, gambling_extras: next});
                                }}
                                placeholder="URL (https://...)"
                                className="text-xs h-8 font-mono" />
                              <label className="flex items-center gap-1.5 text-[11px] text-amber-800 cursor-pointer">
                                <input type="checkbox" checked={!!it.dofollow}
                                  onChange={(e) => {
                                    const next = [...(form.gambling_extras||[])];
                                    next[idx] = {...next[idx], dofollow: e.target.checked};
                                    setForm({...form, gambling_extras: next});
                                  }} />
                                <span>Dofollow (indeksuj)</span>
                              </label>
                            </div>
                            <Button size="sm" variant="ghost"
                              onClick={() => setForm({...form, gambling_extras: (form.gambling_extras||[]).filter((_,i)=>i!==idx)})}
                              className="h-8 w-8 p-0 text-red-500">
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <Separator />

                <Button data-testid="generate-btn" className="w-full h-11 rounded-lg font-medium gap-2"
                  onClick={handleSmartGenerate} disabled={generating || (!form.topic.trim() && !form.use_trends)}>
                  {generating ? (
                    <><Loader2 className="w-4 h-4 animate-spin" />{t("generator.generating")}</>
                  ) : (
                    <><Sparkles className="w-4 h-4" strokeWidth={1.5} />{t("generator.generate")}</>
                  )}
                </Button>
              </CardContent>
            </Card>

            {/* Podglad */}
            <Card className="lg:col-span-3 rounded-xl border shadow-sm bg-card">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-base font-heading">{t("generator.preview")}</CardTitle>
                  {result && (
                    <div className="flex items-center gap-2">
                      <Badge variant="secondary" className="rounded-md text-xs">{result.word_count} {t("generator.wordsGenerated")}</Badge>
                      <div className="flex rounded-lg border overflow-hidden">
                        <button onClick={() => setPreviewMode("preview")}
                          className={`px-3 py-1 text-[10px] font-medium transition-colors ${previewMode === "preview" ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}>
                          Podglad
                        </button>
                        <button onClick={() => setPreviewMode("html")}
                          className={`px-3 py-1 text-[10px] font-medium transition-colors ${previewMode === "html" ? "bg-primary text-white" : "text-muted-foreground hover:bg-muted"}`}>
                          Kod HTML
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-0">
                {!result ? (
                  <div className="flex flex-col items-center justify-center h-[400px] text-muted-foreground">
                    <Sparkles className="w-12 h-12 mb-4 opacity-20" strokeWidth={1} />
                    <p className="text-sm">{t("generator.noContent")}</p>
                  </div>
                ) : (
                  <>
                    {/* SEO Meta */}
                    {result.seo_meta && (
                      <div className="p-3 rounded-lg bg-emerald-50 dark:bg-emerald-900/10 border border-emerald-200 dark:border-emerald-800 mb-3 space-y-1.5">
                        <p className="text-[9px] font-semibold text-emerald-700 dark:text-emerald-400 uppercase tracking-wider">SEO META</p>
                        <p className="text-xs"><span className="font-medium text-muted-foreground">Title:</span> {result.seo_meta.meta_title}</p>
                        <p className="text-xs"><span className="font-medium text-muted-foreground">Desc:</span> {result.seo_meta.meta_description}</p>
                        {result.seo_meta.focus_keyword && (
                          <p className="text-xs"><span className="font-medium text-muted-foreground">Focus:</span> {result.seo_meta.focus_keyword}</p>
                        )}
                        <div className="flex flex-wrap gap-1">
                          {(result.seo_meta.tags || []).map((tag, i) => (
                            <Badge key={i} variant="outline" className="text-[9px] rounded-md">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Zdjecie */}
                    {/* Zdjęcie główne - z możliwością zmiany */}
                    {result.featured_image && (
                      <div className="p-3 rounded-lg bg-blue-50 dark:bg-blue-900/10 border border-blue-200 dark:border-blue-800 mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-14 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center shrink-0 overflow-hidden">
                            {result.featured_image.url ? (
                              <img 
                                src={result.featured_image.url} 
                                alt={result.featured_image.alt || ""} 
                                className="w-full h-full object-cover"
                                onError={(e) => { e.target.style.display = 'none'; }}
                              />
                            ) : (
                              <ImageIcon className="w-5 h-5 text-slate-400" strokeWidth={1} />
                            )}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-[9px] font-semibold text-blue-700 dark:text-blue-400 uppercase tracking-wider">ZDJECIE GLOWNE</p>
                            <p className="text-xs font-medium text-foreground truncate">{result.featured_image.filename || "Brak nazwy"}</p>
                            <p className="text-[10px] text-muted-foreground line-clamp-1">alt: "{result.featured_image.alt}"</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button variant="ghost" size="icon" className="h-7 w-7"
                              onClick={() => openImagePicker("result")}
                              title="Zmień zdjęcie z biblioteki">
                              <FolderOpen className="w-3.5 h-3.5" />
                            </Button>
                            <label className="cursor-pointer">
                              <input type="file" accept="image/*" className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onload = async (ev) => {
                                      const base64 = ev.target.result;
                                      // Aktualizuj featured_image w result
                                      setResult(prev => ({
                                        ...prev,
                                        featured_image: {
                                          ...prev.featured_image,
                                          url: base64,
                                          filename: file.name,
                                          uploaded: true
                                        }
                                      }));
                                      toast.success("Zdjęcie zmienione");
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                              <Button variant="ghost" size="icon" className="h-7 w-7" asChild>
                                <span title="Wgraj z komputera">
                                  <Upload className="w-3.5 h-3.5" />
                                </span>
                              </Button>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Jeśli brak zdjęcia - pokaż opcje dodania */}
                    {!result.featured_image && (
                      <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-900/10 border border-amber-200 dark:border-amber-800 mb-3">
                        <div className="flex items-center gap-3">
                          <div className="w-20 h-14 rounded-lg bg-slate-200 dark:bg-slate-700 flex items-center justify-center shrink-0">
                            <ImageIcon className="w-5 h-5 text-slate-400" strokeWidth={1} />
                          </div>
                          <div className="flex-1">
                            <p className="text-xs font-medium text-foreground">Brak zdjęcia głównego</p>
                            <p className="text-[10px] text-muted-foreground">Dodaj zdjęcie do artykułu</p>
                          </div>
                          <div className="flex gap-1 shrink-0">
                            <Button variant="outline" size="sm" className="h-7 text-xs gap-1"
                              onClick={() => openImagePicker("result")}>
                              <FolderOpen className="w-3 h-3" /> Biblioteka
                            </Button>
                            <label className="cursor-pointer">
                              <input type="file" accept="image/*" className="hidden"
                                onChange={async (e) => {
                                  const file = e.target.files?.[0];
                                  if (file) {
                                    const reader = new FileReader();
                                    reader.onload = async (ev) => {
                                      const base64 = ev.target.result;
                                      setResult(prev => ({
                                        ...prev,
                                        featured_image: {
                                          url: base64,
                                          filename: file.name,
                                          alt: prev.title || "",
                                          uploaded: true
                                        }
                                      }));
                                      toast.success("Zdjęcie dodane");
                                    };
                                    reader.readAsDataURL(file);
                                  }
                                }}
                              />
                              <Button variant="outline" size="sm" className="h-7 text-xs gap-1" asChild>
                                <span><Upload className="w-3 h-3" /> Upload</span>
                              </Button>
                            </label>
                          </div>
                        </div>
                      </div>
                    )}

                    {/* Artykul */}
                    <ScrollArea className="h-[320px] pr-4">
                      {previewMode === "preview" ? (
                        <div className="bg-white dark:bg-slate-950 rounded-lg border p-6 shadow-inner">
                          <h1 className="text-2xl font-bold text-slate-900 dark:text-white mb-4 leading-tight font-heading">
                            {result.title}
                          </h1>
                          <SafeHtml className="prose prose-sm max-w-none dark:prose-invert
                            [&_.lead]:text-lg [&_.lead]:text-slate-600 [&_.lead]:dark:text-slate-300 [&_.lead]:leading-relaxed [&_.lead]:mb-6 [&_.lead]:font-medium
                            [&_blockquote]:border-l-4 [&_blockquote]:border-primary [&_blockquote]:pl-4 [&_blockquote]:italic [&_blockquote]:bg-slate-50 [&_blockquote]:dark:bg-slate-800/50 [&_blockquote]:py-2 [&_blockquote]:rounded-r
                            [&_.tip]:bg-amber-50 [&_.tip]:dark:bg-amber-900/20 [&_.tip]:border-l-4 [&_.tip]:border-amber-500 [&_.tip]:p-4 [&_.tip]:my-4 [&_.tip]:rounded-r
                            [&_.faq]:bg-blue-50 [&_.faq]:dark:bg-blue-900/20 [&_.faq]:p-4 [&_.faq]:my-4 [&_.faq]:rounded-lg
                            [&_.faq_h3]:font-semibold [&_.faq_h3]:text-blue-700 [&_.faq_h3]:dark:text-blue-300 [&_.faq_h3]:mb-2"
                            html={result.content}
                          />
                        </div>
                      ) : (
                        <div className="rounded-lg border bg-slate-950 p-4">
                          <pre className="text-[11px] text-emerald-400 font-mono whitespace-pre-wrap break-words leading-relaxed">
                            {result.content}
                          </pre>
                        </div>
                      )}
                    </ScrollArea>

                    <Separator className="my-3" />

                    {/* Akcje publikacji */}
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Button variant="outline" size="sm" data-testid="copy-btn" onClick={handleCopy} className="gap-2 rounded-lg">
                          <Copy className="w-3.5 h-3.5" strokeWidth={1.5} /> {t("generator.copyContent")}
                        </Button>
                        <Button variant="outline" size="sm" className="gap-2 rounded-lg"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/posts/${result.post_id}/to-queue`);
                              toast.success("Przekazano do kolejki");
                            } catch (err) { toast.error(t("common.error")); }
                          }}>
                          <FileText className="w-3.5 h-3.5" strokeWidth={1.5} /> Do kolejki
                        </Button>
                        <Button size="sm" className="gap-2 rounded-lg"
                          onClick={async () => {
                            try {
                              await axios.post(`${API}/posts/${result.post_id}/publish`);
                              toast.success("Opublikowano!");
                            } catch (err) { toast.error(t("common.error")); }
                          }}>
                          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.5} /> Publikuj teraz
                        </Button>
                      </div>

                      {/* Zaplanuj publikacje */}
                      <div className="flex items-center gap-2 p-3 rounded-lg bg-muted/50">
                        <Input type="datetime-local" value={scheduleDate}
                          onChange={(e) => setScheduleDate(e.target.value)}
                          className="h-8 text-xs flex-1" />
                        <Button variant="outline" size="sm" className="gap-1 text-xs shrink-0"
                          disabled={!scheduleDate || scheduling}
                          onClick={async () => {
                            setScheduling(true);
                            try {
                              await axios.post(`${API}/posts/${result.post_id}/schedule`, { scheduled_at: scheduleDate });
                              toast.success(`Zaplanowano na ${new Date(scheduleDate).toLocaleString()}`);
                            } catch (err) { toast.error(t("common.error")); }
                            finally { setScheduling(false); }
                          }}>
                          {scheduling ? <Loader2 className="w-3 h-3 animate-spin" /> : <TrendingUp className="w-3 h-3" />}
                          Zaplanuj
                        </Button>
                      </div>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* ========== ZAKLADKA: RECZNIE PUBLIKUJ ========== */}
        <TabsContent value="publish" className="mt-4">
          <div className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <PenTool className="w-5 h-5" /> Reczna publikacja artykulu
              </h2>
              <div className="flex items-center gap-2">
                <Button variant="outline" size="sm" onClick={handleSaveDraft} disabled={pubSaving || pubPublishing}>
                  {pubSaving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
                  Zapisz szkic
                </Button>
                <Button size="sm" onClick={handlePublish} disabled={pubSaving || pubPublishing || !pubSelectedSiteId}>
                  {pubPublishing ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Send className="w-4 h-4 mr-1" />}
                  Publikuj
                </Button>
              </div>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
              {/* Lewa kolumna - Edytor */}
              <div className="lg:col-span-2 space-y-4">
                {/* Tytul */}
                <Input
                  placeholder="Tytul artykulu..."
                  value={pubTitle}
                  onChange={(e) => setPubTitle(e.target.value)}
                  className="text-xl font-semibold h-12"
                  data-testid="pub-title-input"
                />

                {/* Instrukcje dla AI */}
                <Card className="border-purple-200 bg-purple-50/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Sparkles className="w-4 h-4 text-purple-600" />
                      Instrukcje dla AI (opcjonalne)
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <Textarea
                      placeholder="Wpisz szczegolowe instrukcje dla AI, np.: Napisz artykul o zaletach pozyczek online. Skup sie na szybkosci procesowania wnioskow i bezpieczenstwie transakcji. Artykul powinien byc napisany w przyjaznym tonie..."
                      value={pubAiPrompt}
                      onChange={(e) => setPubAiPrompt(e.target.value)}
                      rows={4}
                      className="text-sm resize-none"
                    />
                    <p className="text-xs text-muted-foreground">
                      Jesli wpiszesz instrukcje, AI wygeneruje tresc na ich podstawie. Fraza kluczowa i link zostana automatycznie uwzglednione.
                    </p>
                  </CardContent>
                </Card>

                {/* Informacja o tłumaczeniu */}
                {pubSourceLanguage && pubSourceLanguage !== "pl" && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-900/20 border border-amber-200">
                    <Globe className="w-4 h-4 text-amber-600" />
                    <span className="text-sm text-amber-700 dark:text-amber-300">
                      Źródło w języku <strong>{pubSourceLanguage.toUpperCase()}</strong> — AI automatycznie przetłumaczy na polski
                    </span>
                  </div>
                )}

                {/* AI Button */}
                <Button
                  variant="default"
                  size="sm"
                  onClick={handleGenerateForPublish}
                  disabled={generating || (!pubTitle.trim() && !pubAiPrompt.trim())}
                  className="gap-1"
                >
                  {generating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />}
                  {pubSourceLanguage && pubSourceLanguage !== "pl" 
                    ? "Napisz AI + przetłumacz na PL" 
                    : (pubAiPrompt.trim() ? "Napisz AI na podstawie instrukcji" : "Napisz AI na podstawie tytulu")}
                </Button>

                {/* Link reklamodawcy */}
                <Card className="border-emerald-200 bg-emerald-50/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <Link2 className="w-4 h-4 text-emerald-600" />
                        Fraza kluczowa i link
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={fetchLinkSuggestion}
                        disabled={loadingLinkSuggestion || refreshingCache || (!pubTitle.trim() && !pubAiPrompt.trim())}
                        className="h-7 px-2 text-xs gap-1 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                        title="AI zaproponuje najlepszy link z Twoich stron"
                      >
                        {loadingLinkSuggestion ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <Sparkles className="w-3.5 h-3.5" />
                        )}
                        Sugeruj AI
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={refreshLinkCache}
                        disabled={refreshingCache}
                        className="h-7 px-2 text-xs gap-1 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
                        title="Odśwież bazę linków z RSS (pobiera najnowsze artykuły)"
                      >
                        {refreshingCache ? (
                          <Loader2 className="w-3.5 h-3.5 animate-spin" />
                        ) : (
                          <RefreshCw className="w-3.5 h-3.5" />
                        )}
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {/* Sugestie AI */}
                    {linkSuggestions.length > 0 && (
                      <div className="space-y-2 p-3 bg-blue-50 rounded-lg border border-blue-200">
                        <div className="flex items-center gap-1.5 text-xs font-medium text-blue-700">
                          <Lightbulb className="w-3.5 h-3.5" />
                          Sugestie AI (kliknij aby użyć)
                        </div>
                        {linkSuggestions.map((s, idx) => (
                          <div 
                            key={idx}
                            onClick={() => applySuggestedLink(s)}
                            className="p-2 bg-white rounded border border-blue-100 cursor-pointer hover:border-blue-400 hover:bg-blue-50/50 transition-colors"
                          >
                            <div className="flex items-center justify-between gap-2">
                              <div className="flex-1 min-w-0">
                                <div className="text-sm font-medium text-slate-800 truncate">
                                  "{s.anchor_text}"
                                </div>
                                <div className="text-xs text-slate-500 truncate flex items-center gap-1">
                                  <ExternalLink className="w-3 h-3" />
                                  {s.target_name || s.url}
                                </div>
                              </div>
                              <div className="flex items-center gap-2 shrink-0">
                                <Badge variant="outline" className={`text-[10px] ${
                                  s.relevance_score >= 80 ? 'bg-emerald-50 text-emerald-700 border-emerald-200' :
                                  s.relevance_score >= 60 ? 'bg-amber-50 text-amber-700 border-amber-200' :
                                  'bg-slate-50 text-slate-600 border-slate-200'
                                }`}>
                                  {s.relevance_score}%
                                </Badge>
                                <Button size="sm" variant="ghost" className="h-6 px-2 text-xs text-blue-600">
                                  Użyj
                                </Button>
                              </div>
                            </div>
                            {s.reason && (
                              <div className="text-[10px] text-slate-400 mt-1 italic">
                                {s.reason}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div>
                        <Label className="text-xs text-slate-500">Fraza kluczowa</Label>
                        <Input
                          placeholder="np. pozyczki online"
                          value={pubKeywordPhrase}
                          onChange={(e) => setPubKeywordPhrase(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                      <div>
                        <Label className="text-xs text-slate-500">URL docelowy</Label>
                        <Input
                          placeholder="https://example.com/landing"
                          value={pubKeywordUrl}
                          onChange={(e) => setPubKeywordUrl(e.target.value)}
                          className="text-sm"
                        />
                      </div>
                    </div>
                    <Button 
                      size="sm" 
                      variant="outline" 
                      onClick={insertKeywordLink}
                      disabled={!pubKeywordPhrase || !pubKeywordUrl}
                      className="gap-1 text-emerald-700 border-emerald-300"
                    >
                      <Plus className="w-3.5 h-3.5" /> Wstaw link do tresci
                    </Button>

                    {/* Sprint 8.M: Gambling mode w Ręcznej publikacji */}
                    <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 dark:bg-amber-950/20 p-3 space-y-2">
                      <label className="flex items-start gap-2 cursor-pointer">
                        <input type="checkbox" className="mt-0.5"
                          data-testid="pub-gambling-checkbox"
                          checked={pubGamblingAd}
                          onChange={(e) => setPubGamblingAd(e.target.checked)} />
                        <div className="flex-1">
                          <div className="text-sm font-semibold text-amber-900 dark:text-amber-300">
                            &#127920; Reklama strony hazardowej
                          </div>
                          <p className="text-[11px] text-amber-700 dark:text-amber-400 mt-0.5 leading-snug">
                            Wymusza tryb edukacyjny (historia, matematyka, jurysdykcje) z linkiem inline z pola URL powyzej.
                            Automatycznie dodaje disclaimer 18+ + info o odpowiedzialnej grze.
                            <strong> Ryzyko prawne: art. 29 ustawy o grach hazardowych.</strong>
                          </p>
                        </div>
                      </label>
                      {pubGamblingAd && (
                        <div className="pt-2 border-t border-amber-200 space-y-2">
                          <div>
                            <Label className="text-xs font-medium">Nazwa marki / operatora</Label>
                            <Input value={pubGamblingBrand}
                              onChange={(e) => setPubGamblingBrand(e.target.value)}
                              placeholder="np. WinAir"
                              className="text-sm" />
                          </div>
                          <label className="flex items-center gap-2 text-xs text-amber-900 cursor-pointer">
                            <input type="checkbox" checked={pubGamblingDofollow}
                              onChange={(e) => setPubGamblingDofollow(e.target.checked)} />
                            <span><strong>Dofollow</strong> (indeksowany link — bez atrybutu rel)</span>
                          </label>
                          <p className="text-[10px] text-amber-600 leading-tight">
                            URL operatora pobrany z pola &quot;URL docelowy&quot; powyzej. Link zostanie wstawiony jako <code>rel=&quot;sponsored nofollow&quot;</code>.
                          </p>

                          <div className="pt-2 border-t border-amber-200 space-y-2">
                            <div className="flex items-center justify-between">
                              <Label className="text-xs font-medium">Dodatkowe linki afiliacyjne (opcjonalne)</Label>
                              <Button size="sm" variant="outline"
                                onClick={() => setPubGamblingExtras([...pubGamblingExtras, {anchor: "", url: ""}])}
                                className="h-7 text-[11px] gap-1">
                                <Plus className="w-3 h-3" /> Dodaj link
                              </Button>
                            </div>
                            {pubGamblingExtras.length === 0 && (
                              <p className="text-[10px] text-amber-600">Brak dodatkowych linków. Kliknij &quot;Dodaj link&quot; aby dodać kolejną parę anchor/URL.</p>
                            )}
                            {pubGamblingExtras.map((it, idx) => (
                              <div key={idx} className="flex gap-1.5 items-start bg-white/50 rounded p-2">
                                <div className="flex-1 space-y-1">
                                  <Input value={it.anchor}
                                    onChange={(e) => {
                                      const next = [...pubGamblingExtras];
                                      next[idx] = {...next[idx], anchor: e.target.value};
                                      setPubGamblingExtras(next);
                                    }}
                                    placeholder="Anchor text (np. 'poradnik ruletki')"
                                    className="text-xs h-8" />
                                  <Input value={it.url}
                                    onChange={(e) => {
                                      const next = [...pubGamblingExtras];
                                      next[idx] = {...next[idx], url: e.target.value};
                                      setPubGamblingExtras(next);
                                    }}
                                    placeholder="URL (https://...)"
                                    className="text-xs h-8 font-mono" />
                                  <label className="flex items-center gap-1.5 text-[11px] text-amber-800 cursor-pointer">
                                    <input type="checkbox" checked={!!it.dofollow}
                                      onChange={(e) => {
                                        const next = [...pubGamblingExtras];
                                        next[idx] = {...next[idx], dofollow: e.target.checked};
                                        setPubGamblingExtras(next);
                                      }} />
                                    <span>Dofollow (indeksuj)</span>
                                  </label>
                                </div>
                                <Button size="sm" variant="ghost"
                                  onClick={() => setPubGamblingExtras(pubGamblingExtras.filter((_,i)=>i!==idx))}
                                  className="h-8 w-8 p-0 text-red-500">
                                  <X className="w-3 h-3" />
                                </Button>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  </CardContent>
                </Card>

                {/* Edytor Tabs */}
                <Tabs value={pubEditorTab} onValueChange={setPubEditorTab} className="border rounded-lg">
                  <TabsList className="w-full justify-start rounded-t-lg rounded-b-none border-b bg-slate-50">
                    <TabsTrigger value="editor" className="gap-1.5">
                      <Bold className="w-3.5 h-3.5" /> Edytor
                    </TabsTrigger>
                    <TabsTrigger value="html" className="gap-1.5">
                      <FileCode className="w-3.5 h-3.5" /> HTML
                    </TabsTrigger>
                    <TabsTrigger value="preview" className="gap-1.5">
                      <Eye className="w-3.5 h-3.5" /> Podglad
                    </TabsTrigger>
                  </TabsList>

                  <TabsContent value="editor" className="m-0">
                    <MenuBar 
                      editor={editor} 
                      onInsertLink={pubKeywordPhrase && pubKeywordUrl ? insertKeywordLink : null} 
                    />
                    <div className="min-h-[350px] max-h-[500px] overflow-y-auto">
                      <EditorContent editor={editor} />
                    </div>
                  </TabsContent>

                  <TabsContent value="html" className="m-0 p-4">
                    <Textarea
                      value={pubHtmlSource}
                      onChange={(e) => setPubHtmlSource(e.target.value)}
                      className="font-mono text-xs min-h-[350px] resize-y"
                      placeholder="<p>Tresc HTML...</p>"
                    />
                    <Button size="sm" onClick={applyHtmlChanges} className="mt-2">
                      Zastosuj zmiany
                    </Button>
                  </TabsContent>

                  <TabsContent value="preview" className="m-0 p-4">
                    <SafeHtml
                      className="prose prose-sm max-w-none min-h-[350px]"
                      html={pubHtmlSource}
                    />
                  </TabsContent>
                </Tabs>

                {/* Statystyki */}
                <div className="flex items-center gap-4 text-xs text-slate-500">
                  <span>{charCount.toLocaleString()} znakow</span>
                  <span>{wordCount.toLocaleString()} slow</span>
                  {charCount < 3500 && charCount > 0 && (
                    <span className="text-amber-600">(zalecane min. 3500)</span>
                  )}
                  {charCount >= 3500 && <span className="text-emerald-600">OK</span>}
                </div>
              </div>

              {/* Prawa kolumna - Ustawienia */}
              <div className="space-y-4">
                {/* Info o źródle RSS */}
                {sourceInfo && (
                  <Card className="border-blue-200 bg-blue-50/50">
                    <CardHeader className="py-2">
                      <CardTitle className="text-xs flex items-center gap-2 text-blue-700">
                        <Rss className="w-3.5 h-3.5" /> Źródło: {sourceInfo.feedName}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-0 pb-2">
                      <p className="text-[10px] text-blue-600/70">
                        Artykuł zostanie wygenerowany na podstawie danych RSS. AI nie będzie zmyślał.
                      </p>
                    </CardContent>
                  </Card>
                )}

                {/* Typ artykułu */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Typ artykułu
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Select value={articleType} onValueChange={setArticleType}>
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {articleTypes.map((type) => (
                          <SelectItem key={type.value} value={type.value}>
                            {type.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </CardContent>
                </Card>

                {/* Wybor strony i kategorii */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Globe className="w-4 h-4" /> Strona docelowa
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Portal</Label>
                      <Popover open={siteSearchOpen} onOpenChange={setSiteSearchOpen}>
                        <PopoverTrigger asChild>
                          <Button variant="outline" role="combobox" className="w-full justify-between font-normal">
                            {pubSelectedSiteId ? sitesWithWpApi.find(s => s.id === pubSelectedSiteId)?.name : "Wybierz strone..."}
                            <Search className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                          </Button>
                        </PopoverTrigger>
                        <PopoverContent className="w-[300px] p-0" align="start">
                          <Command>
                            <CommandInput placeholder="Szukaj strony..." value={siteSearchQuery} onValueChange={setSiteSearchQuery} />
                            <CommandList>
                              <CommandEmpty>Nie znaleziono strony</CommandEmpty>
                              <CommandGroup>
                                {sitesWithWpApi
                                  .filter(s => s.name.toLowerCase().includes(siteSearchQuery.toLowerCase()))
                                  .map((site) => (
                                  <CommandItem
                                    key={site.id}
                                    value={site.name}
                                    onSelect={() => {
                                      setPubSelectedSiteId(site.id);
                                      setSiteSearchOpen(false);
                                      setSiteSearchQuery("");
                                    }}
                                  >
                                    <Check className={"mr-2 h-4 w-4 " + (pubSelectedSiteId === site.id ? "opacity-100" : "opacity-0")} />
                                    {site.name}
                                    {site.site_type === "emergent" && <Badge variant="secondary" className="ml-2 text-xs">Emergent</Badge>}
                                  </CommandItem>
                                ))}
                              </CommandGroup>
                            </CommandList>
                          </Command>
                        </PopoverContent>
                      </Popover>
                    </div>

                    {pubSelectedSiteId && (
                      <div>
                        <Label className="text-xs text-slate-500 mb-1 block">Kategoria WordPress</Label>
                        {loadingCategories ? (
                          <div className="flex items-center gap-2 text-xs text-slate-400 py-2">
                            <Loader2 className="w-3 h-3 animate-spin" /> Ladowanie kategorii...
                          </div>
                        ) : wpCategories.length > 0 ? (
                          <Select value={pubSelectedCategoryId} onValueChange={setPubSelectedCategoryId}>
                            <SelectTrigger>
                              <SelectValue placeholder="Wybierz kategorie..." />
                            </SelectTrigger>
                            <SelectContent>
                              {wpCategories.map((cat) => (
                                <SelectItem key={cat.id} value={String(cat.id)}>
                                  {cat.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        ) : (
                          <p className="text-xs text-slate-400">Brak kategorii</p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>

                {/* Obrazek wyrozniajacy */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <ImageIcon className="w-4 h-4" /> Obrazek wyrozniajacy
                      {sourceInfo?.imageUrl && pubFeaturedImage === sourceInfo.imageUrl && (
                        <Badge variant="outline" className="text-[9px] ml-auto border-emerald-300 text-emerald-600 bg-emerald-50">
                          <Rss className="w-2.5 h-2.5 mr-1" /> z RSS
                        </Badge>
                      )}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {pubFeaturedImage && (
                      <div className="relative rounded-lg overflow-hidden aspect-video bg-slate-100">
                        <img src={pubFeaturedImage} alt="" className="w-full h-full object-cover" />
                        {/* Znacznik źródła RSS na zdjęciu */}
                        {sourceInfo?.imageUrl && pubFeaturedImage === sourceInfo.imageUrl && (
                          <div className="absolute top-2 left-2 bg-emerald-500 text-white text-[10px] px-2 py-1 rounded flex items-center gap-1">
                            <Rss className="w-3 h-3" /> Zdjęcie z RSS
                          </div>
                        )}
                        <Button
                          variant="destructive"
                          size="sm"
                          className="absolute top-2 right-2 h-6 w-6 p-0"
                          onClick={() => setPubFeaturedImage("")}
                        >
                          <X className="w-3 h-3" />
                        </Button>
                      </div>
                    )}
                    
                    {/* Przycisk użycia zdjęcia z RSS */}
                    {sourceInfo?.imageUrl && !pubFeaturedImage && (
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="w-full gap-1.5 border-blue-300 text-blue-600"
                        onClick={() => setPubFeaturedImage(sourceInfo.imageUrl)}
                      >
                        <Rss className="w-3.5 h-3.5" /> Użyj zdjęcia z RSS
                      </Button>
                    )}
                    
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm" onClick={openImagePicker} className="flex-1 gap-1.5">
                        <FolderOpen className="w-4 h-4" /> Z biblioteki
                      </Button>
                      <label className="flex-1">
                        <input
                          type="file"
                          accept="image/*"
                          className="hidden"
                          onChange={handleFileUpload}
                          disabled={uploadingImage}
                        />
                        <Button variant="outline" size="sm" className="w-full gap-1.5" asChild>
                          <span>
                            {uploadingImage ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                            Wgraj
                          </span>
                        </Button>
                      </label>
                    </div>
                  </CardContent>
                </Card>

                {/* SEO */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center justify-between">
                      <span className="flex items-center gap-2">
                        <Search className="w-4 h-4" /> SEO
                      </span>
                      <Button 
                        variant="outline" 
                        size="sm" 
                        className="h-7 text-xs gap-1.5 text-violet-600 hover:text-violet-700 border-violet-200 hover:border-violet-300"
                        onClick={generateSeoMetaWithAI}
                        disabled={generatingSeoMeta || (!pubTitle.trim() && !pubContent.trim())}
                      >
                        {generatingSeoMeta ? (
                          <Loader2 className="w-3 h-3 animate-spin" />
                        ) : (
                          <Sparkles className="w-3 h-3" />
                        )}
                        Uzupełnij z AI
                      </Button>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Alt obrazka (SEO)</Label>
                      <Input
                        placeholder="Opis obrazka dla SEO..."
                        value={pubImageAlt}
                        onChange={(e) => setPubImageAlt(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Meta tytul ({pubMetaTitle.length}/60)</Label>
                      <Input
                        placeholder="Tytul SEO..."
                        value={pubMetaTitle}
                        onChange={(e) => setPubMetaTitle(e.target.value.substring(0, 60))}
                        className="text-sm"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block">Meta opis ({pubMetaDescription.length}/155)</Label>
                      <Textarea
                        placeholder="Opis SEO..."
                        value={pubMetaDescription}
                        onChange={(e) => setPubMetaDescription(e.target.value.substring(0, 155))}
                        className="text-sm min-h-[60px]"
                      />
                    </div>
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                        <Tag className="w-3 h-3" /> Tagi (przecinek)
                      </Label>
                      <Input
                        placeholder="seo, marketing, content..."
                        value={pubTags}
                        onChange={(e) => setPubTags(e.target.value)}
                        className="text-sm"
                      />
                    </div>
                  </CardContent>
                </Card>

                {/* Podpis autora */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <User className="w-4 h-4" /> Autor
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <Input
                      placeholder="Imie i nazwisko autora..."
                      value={pubAuthorName}
                      onChange={(e) => setPubAuthorName(e.target.value)}
                      className="text-sm"
                    />
                  </CardContent>
                </Card>

                {/* Artykuly pomocnicze */}
                <Card>
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <FileText className="w-4 h-4" /> Artykuly pomocnicze
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {pubSupportArticles.length > 0 && (
                      <div className="space-y-2">
                        {pubSupportArticles.map((article) => (
                          <div key={article.id} className="flex items-center justify-between p-2 rounded-lg bg-slate-50 border text-xs">
                            <span className="truncate flex-1 mr-2">{article.title}</span>
                            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => removeSupportArticle(article.id)}>
                              <X className="w-3 h-3" />
                            </Button>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="w-full gap-1.5"
                      onClick={() => {
                        setShowPostPicker(true);
                        fetchAvailablePosts();
                      }}
                    >
                      <Plus className="w-4 h-4" /> Dodaj artykul
                    </Button>
                  </CardContent>
                </Card>

                {/* Planowanie publikacji */}
                <Card className="border-amber-200 bg-amber-50/50">
                  <CardHeader className="py-3">
                    <CardTitle className="text-sm flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-amber-600" /> Zaplanuj publikacje
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    <div>
                      <Label className="text-xs text-slate-500 mb-1 block flex items-center gap-1">
                        <Clock className="w-3 h-3" /> Data i godzina publikacji
                      </Label>
                      <Input
                        type="datetime-local"
                        value={pubScheduleDate}
                        onChange={(e) => setPubScheduleDate(e.target.value)}
                        className="text-sm"
                        data-testid="schedule-datetime-input"
                        min={new Date().toISOString().slice(0, 16)}
                      />
                    </div>
                    <p className="text-[10px] text-amber-700/70">
                      WordPress automatycznie opublikuje artykul o wyznaczonej porze.
                    </p>
                    <Button
                      variant="outline"
                      size="sm"
                      className="w-full gap-1.5 border-amber-300 text-amber-700 hover:bg-amber-100"
                      onClick={handleSchedulePost}
                      disabled={pubScheduling || pubPublishing || !pubSelectedSiteId || !pubScheduleDate || !pubTitle.trim()}
                      data-testid="schedule-post-btn"
                    >
                      {pubScheduling ? (
                        <><Loader2 className="w-4 h-4 animate-spin" /> Planowanie...</>
                      ) : (
                        <><Calendar className="w-4 h-4" /> Dodaj do kolejki</>
                      )}
                    </Button>
                  </CardContent>
                </Card>
              </div>
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Dialog wyboru obrazka z biblioteki */}
      {showImagePicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-none max-h-[80vh] flex flex-col">
            <CardHeader className="py-3 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FolderOpen className="w-5 h-5" /> Biblioteka mediow
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowImagePicker(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 flex-1 overflow-hidden flex flex-col">
              <div className="flex flex-wrap gap-2 mb-4">
                <Button
                  variant={selectedMediaCategory === "" ? "default" : "outline"}
                  size="sm"
                  onClick={() => { setSelectedMediaCategory(""); fetchMediaImages(""); }}
                >
                  Wszystkie
                </Button>
                {mediaCategories.map((cat) => (
                  <Button
                    key={cat}
                    variant={selectedMediaCategory === cat ? "default" : "outline"}
                    size="sm"
                    onClick={() => { setSelectedMediaCategory(cat); fetchMediaImages(cat); }}
                  >
                    {cat}
                  </Button>
                ))}
              </div>
              
              <div className="flex-1 overflow-y-auto">
                {loadingMedia ? (
                  <div className="flex items-center justify-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                  </div>
                ) : mediaImages.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                    <ImageIcon className="w-12 h-12 mb-3 opacity-30" />
                    <p className="text-sm">Brak obrazkow</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-3">
                    {mediaImages.map((img) => (
                      <button
                        key={img.id}
                        onClick={() => selectMediaImage(img)}
                        className="relative aspect-square rounded-lg overflow-hidden border-2 border-transparent hover:border-blue-500 transition-all group bg-slate-100"
                      >
                        <img
                          src={img.url?.startsWith("http") ? img.url : `https://epidemos.pl${img.url}`}
                          alt={img.alt || img.filename}
                          className="w-full h-full object-cover"
                        />
                        <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-colors flex items-center justify-center">
                          <span className="text-white text-xs font-medium opacity-0 group-hover:opacity-100">
                            Wybierz
                          </span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              
              <div className="mt-4 pt-4 border-t flex-shrink-0">
                <label className="flex items-center justify-center gap-2 p-4 border-2 border-dashed rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50/50">
                  <input type="file" accept="image/*" className="hidden" onChange={handleFileUpload} disabled={uploadingImage} />
                  {uploadingImage ? (
                    <><Loader2 className="w-5 h-5 animate-spin text-blue-500" /><span className="text-sm text-blue-600">Wgrywanie...</span></>
                  ) : (
                    <><Upload className="w-5 h-5 text-slate-400" /><span className="text-sm text-slate-600">Kliknij aby wgrac</span></>
                  )}
                </label>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Dialog wyboru artykulu pomocniczego */}
      {showPostPicker && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <Card className="w-full max-w-none max-h-[80vh] flex flex-col">
            <CardHeader className="py-3 border-b flex-shrink-0">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2">
                  <FileText className="w-5 h-5" /> Wybierz artykul pomocniczy
                </CardTitle>
                <Button variant="ghost" size="sm" onClick={() => setShowPostPicker(false)}>
                  <X className="w-4 h-4" />
                </Button>
              </div>
            </CardHeader>
            <CardContent className="p-4 flex-1 overflow-y-auto">
              {loadingPosts ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="w-8 h-8 animate-spin text-slate-400" />
                </div>
              ) : availablePosts.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 text-slate-500">
                  <FileText className="w-12 h-12 mb-3 opacity-30" />
                  <p className="text-sm">Brak opublikowanych artykulow</p>
                </div>
              ) : (
                <div className="space-y-2">
                  {availablePosts.map((post) => (
                    <button
                      key={post.id}
                      onClick={() => addSupportArticle(post)}
                      disabled={pubSupportArticles.find(a => a.id === post.id)}
                      className="w-full text-left p-3 rounded-lg border hover:bg-slate-50 transition-colors disabled:opacity-50"
                    >
                      <p className="text-sm font-medium truncate">{post.title}</p>
                      <p className="text-xs text-slate-500 truncate">{post.site_name || "—"}</p>
                    </button>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
